import { createSearchParams, useNavigate } from 'react-router-dom'
import { Form, Offcanvas, Spinner, Toast, ToastContainer } from 'react-bootstrap'
import classNames from 'classnames'
import React, { PropsWithChildren, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { Button, ButtonProps } from '../../../../../components/Button/Button'
import { useMeetingWorkflowLauncher } from '../../../../../components/CustomWorkflows/useMeetingWorkflowLauncher'
import { workflowMenuLabel } from '../../../../../components/CustomWorkflows/workflowMenuLabel'
import { MeetingWorkflowsMenu } from '../../../../../components/CustomWorkflows/MeetingWorkflowsMenu'
import {
  CustomDropdownItemOption,
  DropDownItemOptionComponentProps,
  SelectDropdown,
  SelectOption,
} from '../../../../../components/SelectDropdown/SelectDropdown'
import { ReactComponent as ZocksIcon } from '../../../../../assets/ZocksIcon.svg'
import { TagElement } from '../../../../../components/Tag/Tag'
import { DeprecatedTemplateIndicator } from '../../../../../components/DeprecatedTemplateIndicator/DeprecatedTemplateIndicator'
import { getMeetingTemplateOptionFlags } from '../../../../../common/meetingTemplate'
import { MeetingTemplateInfo } from '../MeetingTemplateInfo/MeetingTemplateInfo'
import { DeprecatedMeetingTemplateInfo } from '../DeprecatedMeetingTemplateInfo/DeprecatedMeetingTemplateInfo'
import { DeleteMeetingModal } from './DeleteMeetingModal'
import { AddMeetingToCRMModal } from '../../../../../views/MeetingSummary/CRM/V2/AddMeetingToCRMModal'
import { UpdateIntegrationObjectButton } from '../../../../../views/UpdateIntegrationObject/UpdateIntegrationObjectButton'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { setSelectedMeetingTemplate } from '../../../../../store/meetingSummary/meetingTemplates'
import { selectIsHistoryMode, clearHistoryMode as clearReduxHistoryMode } from '../../../../../store/meetingSummary/contactUpdate'
import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import {
  useHeaderCommentsButton,
  useHeaderCopyAllResults,
  useHeaderCRMExport,
  useHeaderExternalUpdatesButton,
  useHeaderExtraOptions,
  useHeaderMeetingTemplateSelector,
  useHeaderNotepadButton,
  useHeaderShareButton,
  useMeetingContentIsLoading,
  useProcessMeeting,
} from './hooks'
import {
  selectSessionFilter,
  selectSessionFilterContactIds,
  selectSessionFilterDateRange,
  selectSessionFilterMeetingKind,
  selectSessionFilterMeetingSources,
  selectSessionFilterMeetingTemplateIds,
  selectSessionFilterPage,
  selectSessionFilterPageSize,
  selectSessionFilterTags,
  selectSessionFilterUserIds,
} from '../../../../../store/sessionFilter/sessionFilter'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useGetAccountQuery } from '../../../../../store/accountUserSettings/accountUserSettings'
import { selectRoom, selectRoomSession, selectRoomSessionHasTranscription } from '../../../../../store/meetingSummary/roomSession'
import { parseMetadata } from '../utils'
import './MeetingSummaryHeader.scss'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../common/integrations'
import { useCurrentUserIntegrations } from '../../../../../views/Integration/useCurrentUserIntegrations'
import { ReactComponent as SyncedTaskStatus } from '../../../../../assets/images/task-status-synced-stroke.svg'
import { TrimMeetingModal } from '../../PastMeetings/TrimMeeting/TrimMeetingModal'
import { useAPIClient } from '../../APIClientProvider'
import { usePollingEffect } from '../../common/hooks/usePollingEffect'
import { Tooltip } from '../../../../../components/Tooltip/Tooltip'
import { IntegrationIcon } from '../../../../../components/IntegrationIcon'
import { TrackEvent, useTracking } from '../../common/tracking'
import { getRoomTrackingDetails } from '../../tracking'
import { displaySidebar } from '../../../../../store/meetingSummary/display'
import { SidebarTitle, SidebarType } from '../Sidebar/models'

export const MeetingSummaryHeader = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const isHistoryMode = useAppSelector(selectIsHistoryMode)
  const selectedUserIds = useAppSelector(selectSessionFilterUserIds)
  const selectedContactIds = useAppSelector(selectSessionFilterContactIds)
  const selectedDateRange = useAppSelector(selectSessionFilterDateRange)
  const selectedMeetingTypeIds = useAppSelector(selectSessionFilterMeetingTemplateIds)
  const selectedFilterTags = useAppSelector(selectSessionFilterTags)
  const selectedMeetingKind = useAppSelector(selectSessionFilterMeetingKind)
  const selectedMeetingSources = useAppSelector(selectSessionFilterMeetingSources)
  const selectedFilter = useAppSelector(selectSessionFilter)
  const selectedPage = useAppSelector(selectSessionFilterPage)
  const selectedPageSize = useAppSelector(selectSessionFilterPageSize)

  const handleBackButtonClicked = () => {
    if (isHistoryMode) {
      dispatch(clearReduxHistoryMode())
      dispatch(displaySidebar({ type: SidebarType.EXTERNAL_UPDATES, title: SidebarTitle.EXTERNAL_UPDATES, icon: 'bi-arrow-repeat' }))
      return
    }

    const searchParams = {
      ...(selectedFilter && { filterId: selectedFilter.id }),
      ...(selectedPage && { page: selectedPage.toString() }),
      ...(selectedPageSize && { pageSize: selectedPageSize.toString() }),
      ...(selectedUserIds && !selectedFilter && { userId: selectedUserIds.join(',') }),
      ...(selectedContactIds && !selectedFilter && { contactId: selectedContactIds.join(',') }),
      ...(selectedMeetingKind && !selectedFilter && { meetingKind: selectedMeetingKind }),
      ...(selectedMeetingSources.length && !selectedFilter && { meetingSources: JSON.stringify(selectedMeetingSources) }),
      ...(selectedDateRange && !selectedFilter && { dateRange: JSON.stringify(selectedDateRange) }),
      ...(selectedMeetingTypeIds.length && !selectedFilter && { meetingTemplates: JSON.stringify(selectedMeetingTypeIds) }),
      ...(selectedFilterTags.length && !selectedFilter && { tags: JSON.stringify(selectedFilterTags) }),
    }
    navigate({
      pathname: '/workspace/past-meetings',
      search: createSearchParams(searchParams).toString(),
    })
  }

  return (
    <div className='d-flex flex-column'>
      <div className='d-flex zocks-pl-2.0 justify-content-between align-items-center'>
        <div className='d-flex align-items-center zocks-gap-2'>
          <div className='d-lg-none'>
            <ZocksIcon />
          </div>
          <div className='d-none align-items-center d-sm-flex zocks-gap-1.25'>
            <Button variant='link' className='back-button' onClick={handleBackButtonClicked}>
              <i className='bi bi-chevron-left' />
            </Button>
          </div>
        </div>
        <div className='d-none d-sm-flex align-items-center gap-3'>
          <ProcessedMeetingCheckbox />
          <MeetingTemplateSelector />
          <ShareButton />
          <ExportButton />
          <NotepadButton />
          <ExternalUpdatesButton />
          <CommentsButton />
          <UpdateIntegrationObjectButton />
          <MeetingWorkflowsMenu />
          <ExtraOptions />
        </div>
        <div className='d-sm-none'>
          <MobileMenu />
        </div>
      </div>
      <ProcessedMeetingHeader />
      <MeetingTemplateInfo skipConfirmation />
      <DeprecatedMeetingTemplateInfo variant='summary' />
    </div>
  )
}

const ProcessedMeetingCheckbox = () => {
  const { processed, handleSwitchMeetingProcessed, isLoading } = useProcessMeeting()

  return (
    <ScopedBlock
      scope={{
        scope: 'room_session:update',
        superScope: ['room_session:admin'],
      }}
      missingScopeAction={MissingScopeAction.HIDE}
    >
      <Form.Check
        id='process-meeting-checkbox'
        className='d-none d-lg-flex align-items-center process-meeting-checkbox py-1 zocks-px-0.75 text-truncate'
        type='checkbox'
      >
        <Form.Check.Label>
          <span className='button-small'>Have you finished processing the meeting?</span>
        </Form.Check.Label>
        {!isLoading ? <Form.Check.Input type='checkbox' checked={processed} onChange={handleSwitchMeetingProcessed} /> : <Spinner size='sm' variant='light' />}
      </Form.Check>
    </ScopedBlock>
  )
}

const ProcessedMeetingHeader = () => {
  const { processed, handleSwitchMeetingProcessed, isLoading } = useProcessMeeting()

  return (
    <ScopedBlock
      scope={{
        scope: 'room_session:update',
        superScope: ['room_session:admin'],
      }}
      missingScopeAction={MissingScopeAction.HIDE}
    >
      <div className='d-lg-none processed-meeting-header'>
        <Form.Check
          id='process-meeting-checkbox'
          className='process-meeting-checkbox align-items-center justify-content-center py-1 zocks-px-0.75 text-truncate'
          type='checkbox'
          checked={processed}
          onChange={handleSwitchMeetingProcessed}
        >
          <Form.Check.Label>
            <span className='button-small'>Have you finished processing the meeting?</span>
          </Form.Check.Label>
          {!isLoading ? (
            <Form.Check.Input type='checkbox' checked={processed} onChange={handleSwitchMeetingProcessed} />
          ) : (
            <Spinner size='sm' variant='light' />
          )}
        </Form.Check>
      </div>
    </ScopedBlock>
  )
}

const MeetingTemplateSelector = () => {
  const dispatch = useAppDispatch()
  const hasTranscription = useAppSelector(selectRoomSessionHasTranscription)
  const roomSession = useAppSelector(selectRoomSession)

  const { tracked } = useTracking()

  const { meetingTemplates, sessionMeetingTemplate, selectedMeetingTemplate } = useHeaderMeetingTemplateSelector('meetingSummary')

  const currentIsDeprecated = sessionMeetingTemplate?.isDeprecated === true

  const options = useMemo(() => {
    return meetingTemplates
      .filter(({ id, isActive }) => !!isActive || sessionMeetingTemplate?.id === id)
      .map(({ id, name }) => ({ id, label: name }))
      .sort((left, right) => left.label.localeCompare(right.label))
  }, [meetingTemplates, sessionMeetingTemplate])

  const _handleChangeMeetingTemplate = (option: SelectOption) => {
    const { id: meetingTemplateId } = option
    dispatch(setSelectedMeetingTemplate(meetingTemplateId))
  }

  const handleChangeMeetingTemplate = tracked(
    _handleChangeMeetingTemplate,
    { eventName: TrackEvent.ROOM_SESSION_MEETING_TYPE_CHANGED, eventDetails: getRoomTrackingDetails(roomSession) },
    {
      eventDetailsExtractor: ({ id: selectedMeetingTemplateId }: SelectOption) => {
        return {
          selectedMeetingTemplateId,
          source: 'meeting-summary',
        }
      },
    },
  )

  const SessionMeetingTemplateDropdownItemOption: React.FC<DropDownItemOptionComponentProps> = useCallback(
    ({ optionId, optionLabel, onClick }) => {
      const deprecatedLabel = meetingTemplates.find((mt) => mt.id === optionId)?.label
      const { isNewVersion, isCurrent } = getMeetingTemplateOptionFlags({
        optionId,
        currentTemplate: sessionMeetingTemplate,
        allTemplates: meetingTemplates,
      })

      return (
        <CustomDropdownItemOption role='button' onClick={onClick} className='d-flex justify-content-between align-items-center gap-2'>
          <span>
            {optionLabel}
            {deprecatedLabel ? <span className='zocks-text-gray-600'> {deprecatedLabel}</span> : null}
          </span>
          <span className='d-flex gap-1 align-items-center'>
            {isNewVersion ? <TagElement className='meeting-template-new-version-tag'>New version</TagElement> : null}
            {isCurrent ? <span className='zocks-text-primary fw-medium paragraph-small'>Current</span> : null}
          </span>
        </CustomDropdownItemOption>
      )
    },
    [meetingTemplates, sessionMeetingTemplate],
  )

  const isLoading = useMeetingContentIsLoading()

  return (
    <Tooltip title={!hasTranscription ? 'Meeting has no transcription' : undefined} placement='bottom'>
      <div className='header-dropdown-container d-none d-xl-block position-relative'>
        <SelectDropdown
          disabled={!hasTranscription || isLoading}
          className='zocks-py-0.375 zocks-px-0.75'
          options={options}
          icon='bi-columns-gap'
          label={selectedMeetingTemplate?.name}
          labelClassName='pe-2'
          onOptionSelected={handleChangeMeetingTemplate}
          DropDownItemOptionComponent={SessionMeetingTemplateDropdownItemOption}
        />
        {currentIsDeprecated ? <DeprecatedTemplateIndicator variant='dot' title='Meeting type has a new version available' /> : null}
      </div>
      <div className='header-dropdown-container d-none d-sm-block d-xl-none'>
        <SelectDropdown
          disabled={!hasTranscription || isLoading}
          className='zocks-py-0.375 zocks-px-0.75'
          options={options}
          icon='bi-columns-gap'
          iconOnly
          onOptionSelected={handleChangeMeetingTemplate}
          DropDownItemOptionComponent={SessionMeetingTemplateDropdownItemOption}
        />
      </div>
    </Tooltip>
  )
}

const ShareButton = () => {
  const { handleShare } = useHeaderShareButton()

  return (
    <>
      <div className='d-none d-xl-block'>
        <Button variant='header' className='button-small zocks-py-0.375 zocks-px-0.75' onClick={handleShare}>
          <i className='bi bi-share' /> Share
        </Button>
      </div>
      <div className='d-none d-sm-block d-xl-none'>
        <Button variant='header' className='button-small zocks-py-0.375 zocks-px-0.75' onClick={handleShare}>
          <i className='bi bi-share' />{' '}
        </Button>
      </div>
    </>
  )
}

const COPY_ALL = 'copy-all'

const ExportButton = () => {
  const { roomSession, selectedMeetingTemplate, showAddMeetingToCRMModal, selectedCRMServiceId, handleShowCRMModal, handleHideCRMModal } = useHeaderCRMExport()
  const { handleCopyAll } = useHeaderCopyAllResults()
  const { meetingExportSupportedIntegrations } = useCurrentUserIntegrations()

  const copyAllOption = useMemo(() => {
    return { id: COPY_ALL, label: 'Copy all results' }
  }, [])

  const options = useMemo(() => {
    const integrationOptions = meetingExportSupportedIntegrations.map(({ serviceId }) => {
      const serviceSyncStatus = roomSession?.syncStatus.filter((status) => status.serviceId === serviceId) ?? []

      return {
        id: serviceId,
        label: `Save to ${serviceId.charAt(0).toUpperCase() + serviceId.slice(1)}`,
        displayLabel: (
          <div className='d-flex gap-2'>
            <div className='d-flex position-relative'>
              <div className='d-flex position-relative'>
                <IntegrationIcon serviceId={serviceId} className='crm-icon' fill='var(--zocks-gray-900)' />
                {serviceSyncStatus.length > 0 ? <i className='export-result-crm-synced-badge bi bi-circle-fill' /> : null}
              </div>
            </div>
            <div>Save to {getIntegrationDisplayName(serviceId)} </div>
          </div>
        ),
      }
    })

    return [copyAllOption, ...integrationOptions]
  }, [copyAllOption, meetingExportSupportedIntegrations, roomSession])

  const handleSelectOption = (option: SelectOption) => {
    const { id } = option
    if (id === COPY_ALL) {
      handleCopyAll()
    } else {
      handleShowCRMModal(id as IntegrationServiceId)
    }
  }

  return (
    <>
      {roomSession && showAddMeetingToCRMModal && selectedCRMServiceId && selectedMeetingTemplate ? (
        <AddMeetingToCRMModal
          serviceId={selectedCRMServiceId}
          roomSession={roomSession}
          meetingTemplate={selectedMeetingTemplate}
          show={showAddMeetingToCRMModal}
          onHide={handleHideCRMModal}
        />
      ) : null}
      <div>
        <div className='header-dropdown-container d-none d-xl-block'>
          <SelectDropdown
            className='zocks-py-0.375 zocks-px-0.75'
            options={options}
            onOptionSelected={handleSelectOption}
            DropDownItemOptionComponent={ExportDropdownItemOptionComponent}
            icon='bi-download'
            label={'Export'}
          />
        </div>
        <div className='header-dropdown-container d-none d-sm-block d-xl-none'>
          <SelectDropdown className='zocks-py-0.375 zocks-px-0.75' options={options} onOptionSelected={handleSelectOption} icon='bi-download' iconOnly />
        </div>
      </div>
    </>
  )
}

const ExportDropdownItemOptionComponent: React.FC<DropDownItemOptionComponentProps> = ({ optionId, displayLabel, onClick, className, disabled }) => (
  <CustomDropdownItemOption
    key={optionId}
    role='button'
    onClick={disabled ? undefined : onClick}
    aria-disabled={disabled}
    className={classNames(className, 'p-2', { 'opacity-50 export-dropdown-item-disabled': disabled })}
  >
    {displayLabel}
  </CustomDropdownItemOption>
)

const NotepadButton = () => {
  const { isLoading, handleOpenNotepad } = useHeaderNotepadButton()

  return (
    <div>
      <ScopedBlock
        scope={{
          scope: 'meeting_note:read',
          superScope: ['meeting_note:admin'],
        }}
        missingScopeAction={MissingScopeAction.DISABLE}
      >
        <Button variant='header' className='button-small zocks-py-0.375 zocks-px-0.75' onClick={handleOpenNotepad} disabled={isLoading}>
          <i className='bi bi-journal-text' />
        </Button>
      </ScopedBlock>
    </div>
  )
}

const ExternalUpdatesButton = () => {
  const { isLoading, handleOpenExternalUpdates } = useHeaderExternalUpdatesButton()

  return (
    <div>
      <ScopedBlock
        scope={{
          scope: 'integration:manage_crm',
          superScope: ['integration:admin'],
        }}
        missingScopeAction={MissingScopeAction.DISABLE}
      >
        <Button variant='header' className='button-small zocks-py-0.375 zocks-px-0.75' onClick={handleOpenExternalUpdates} disabled={isLoading}>
          <SyncedTaskStatus width={18} height={18} stroke='#000' />
        </Button>
      </ScopedBlock>
    </div>
  )
}

const CommentsButton = () => {
  const accountId = useAppSelector(selectAccountId)
  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery({ accountId })
  const { isLoading: isHeadersLoading, commentCount, handleOpenComments } = useHeaderCommentsButton()

  const isLoading = isAccountLoading || isHeadersLoading
  const isDisabled = account?.data.enterpriseConfig.meetingSummaryConfig.disableComments

  return (
    <>
      <div className='d-none d-xl-block'>
        <ScopedBlock
          scope={{
            scope: 'comment:read',
            superScope: ['comment:admin'],
          }}
          missingScopeAction={MissingScopeAction.DISABLE}
        >
          <Button variant='header' className='button-small zocks-py-0.375 zocks-px-0.75' onClick={handleOpenComments} disabled={isLoading || isDisabled}>
            <i className='bi bi-chat-square-text' /> ({commentCount})
          </Button>
        </ScopedBlock>
      </div>
      <div className='d-none d-sm-block d-xl-none'>
        <ScopedBlock
          scope={{
            scope: 'comment:read',
            superScope: ['comment:admin'],
          }}
          missingScopeAction={MissingScopeAction.DISABLE}
        >
          <Button variant='header' className='button-small zocks-py-0.375 zocks-px-0.75' onClick={handleOpenComments} disabled={isLoading || isDisabled}>
            <i className='bi bi-chat-square-text' />{' '}
          </Button>
        </ScopedBlock>
      </div>
    </>
  )
}

const ExtraOptions = () => {
  const { hasDeleteSessionScope, handleTrimMeeting, handleDeleteMeeting, hasTrimSessionScope, modal, closeModal } = useHeaderExtraOptions()
  const room = useAppSelector(selectRoom)
  const roomSession = useAppSelector(selectRoomSession)
  const hasTranscription = useAppSelector(selectRoomSessionHasTranscription)

  const zocksProcessing = useMemo(() => {
    if (!roomSession) {
      return
    }
    const sessionMetadata = parseMetadata(roomSession.metadata)
    return !!sessionMetadata['zocksProcessing']
  }, [roomSession])

  const options: SelectOption[] = useMemo(() => {
    return [
      {
        id: 'trim-meeting',
        label: 'Trim meeting',
        className: !hasTrimSessionScope || zocksProcessing || !hasTranscription ? 'zocks-text-gray-500' : 'zocks-text-danger',
        disabled: !hasTrimSessionScope || zocksProcessing || !hasTranscription,
        tooltipLabel: !hasTranscription ? 'Meeting has no transcription' : undefined,
      },
      {
        id: 'delete-meeting',
        label: 'Delete meeting',
        className: !hasDeleteSessionScope || zocksProcessing ? 'zocks-text-gray-500' : 'zocks-text-danger',
        disabled: !hasDeleteSessionScope || zocksProcessing,
      },
    ]
  }, [hasDeleteSessionScope, hasTrimSessionScope, zocksProcessing, hasTranscription])

  const ExtraOptionsDropdownItemOptions: React.FC<DropDownItemOptionComponentProps> = useCallback(
    ({ optionId, optionLabel, onClick, disabled, tooltipLabel }) => {
      const option = options.find((option) => option.id === optionId)
      if (!option) {
        return null
      }

      return (
        <Tooltip title={tooltipLabel}>
          <CustomDropdownItemOption role='button' onClick={!disabled ? onClick : undefined} className={`${option.className ?? ''}`}>
            {optionLabel}
          </CustomDropdownItemOption>
        </Tooltip>
      )
    },
    [options],
  )

  const [showToast, setShowToast] = useState(false)
  const navigate = useNavigate()

  const handleOptionSelected = async (option: SelectOption) => {
    const { id: optionId } = option
    switch (optionId) {
      case 'trim-meeting':
        handleTrimMeeting()
        return
      case 'delete-meeting':
        handleDeleteMeeting()
        return
    }
  }

  const { mediaClient } = useAPIClient()
  const [hasAllocatedTranscript, setAllocatedTranscript] = useState(false)
  const {
    started: isPollingForAllocatedTranscripts,
    start: startPollingForAllocatedTranscripts,
    stop: stopPollingForAllocatedTranscripts,
  } = usePollingEffect(
    async () => {
      if (!roomSession) {
        return
      }
      const files = await mediaClient.listSessionFiles(roomSession.roomId, roomSession.id)
      const allocatedFiles = files.filter((file) => {
        const metadata = file.metadata ? JSON.parse(file.metadata) : {}
        return !metadata.unknownParticipants && file.kind === 'transcription'
      })
      if (allocatedFiles.length > 0) {
        setAllocatedTranscript(true)
      }
    },
    [],
    { interval: 5000 },
  )

  useEffect(() => {
    if (hasAllocatedTranscript) {
      stopPollingForAllocatedTranscripts()
      closeModal()
      navigate(0)
    }
  }, [hasAllocatedTranscript, closeModal, navigate, stopPollingForAllocatedTranscripts])

  return (
    <div className='extra-dropdown-container d-none d-sm-block'>
      <DeleteMeetingModal show={modal === 'deleteMeeting'} onHide={closeModal} />
      {modal === 'trimMeeting' && roomSession ? (
        <TrimMeetingModal
          onTrimmingCompleted={() => {
            startPollingForAllocatedTranscripts()
          }}
          room={room}
          roomSession={roomSession}
          show={modal === 'trimMeeting'}
          isPollingForAllocatedTranscripts={isPollingForAllocatedTranscripts}
          onHide={closeModal}
        />
      ) : null}
      <ToastContainer position='bottom-center' className='p-3 summary-toast' style={{ position: 'fixed' }}>
        <Toast onClose={() => setShowToast(false)} show={showToast} delay={3000} autohide>
          <Toast.Body className='d-flex align-items-center'>
            <div className='d-flex align-items-center justify-content-center me-3'>
              <i className='bi bi-check-circle-fill zocks-text-success' />
            </div>
            <span className='flex-grow-1 fw-medium'>Trimming complete. Meeting has been updated successfully.</span>
            <div className='separator mx-3' />
            <button type='button' className='btn-close btn-close-white' aria-label='Close' onClick={() => setShowToast(false)} />
          </Toast.Body>
        </Toast>
      </ToastContainer>
      <SelectDropdown
        className='zocks-py-0.375 zocks-px-0.75'
        options={options}
        icon='bi-three-dots-vertical'
        iconOnly
        hideArrows
        DropDownItemOptionComponent={ExtraOptionsDropdownItemOptions}
        onOptionSelected={handleOptionSelected}
      />
    </div>
  )
}

const MobileMenu = () => {
  const [showMobileMenu, setShowMobileMenu] = useState<boolean>(false)

  const hideMenu = () => setShowMobileMenu(false)

  const meetingWorkflowLauncher = useMeetingWorkflowLauncher()

  return (
    <>
      {meetingWorkflowLauncher.runModal}
      <Button variant='borderless' onClick={() => setShowMobileMenu((showMobileMenu) => !showMobileMenu)}>
        <i className='bi bi-list' />
      </Button>
      <Offcanvas className='h-100 mt-10' show={showMobileMenu} placement={'top'} onHide={hideMenu} backdropClassName='d-none' scroll>
        <div className='d-flex flex-column p-3'>
          <div className='d-flex align-items-center justify-content-end'>
            <Button variant='link' className='button-link-small text-decoration-none py-3' onClick={hideMenu}>
              <i className='bi bi-x-lg' /> Close
            </Button>
          </div>
          <MobileMeetingTemplateSelector />
          <MobileShareButton />
          <MobileExportButton />
          <MobileNotepadButton />
          <MobileCommentsButton />
          {meetingWorkflowLauncher.enabled
            ? meetingWorkflowLauncher.workflows.map((workflow) => (
                <MobileMenuButton
                  key={workflow.id}
                  onClick={() => {
                    meetingWorkflowLauncher.launch(workflow.id)
                    hideMenu()
                  }}
                >
                  <div className='d-flex gap-2 align-items-center'>
                    <i className='bi bi-stars' />
                    {workflowMenuLabel(workflow)}
                  </div>
                </MobileMenuButton>
              ))
            : null}
          <MobileExtraOptions />
        </div>
      </Offcanvas>
    </>
  )
}

const MobileMeetingTemplateSelector: React.FC = () => {
  const dispatch = useAppDispatch()

  const { meetingTemplates, sessionMeetingTemplate, selectedMeetingTemplate } = useHeaderMeetingTemplateSelector('meetingSummary')

  const handleSelectMeetingTemplate = (meetingTemplateId: string) => {
    dispatch(setSelectedMeetingTemplate(meetingTemplateId))
  }

  const menuItems = (
    <>
      {meetingTemplates.map((meetingTemplate) => {
        const showTag = sessionMeetingTemplate?.id === meetingTemplate.id
        return (
          <MobileMenuItem key={meetingTemplate.id} onClick={() => handleSelectMeetingTemplate(meetingTemplate.id)}>
            {meetingTemplate.name}
            {showTag ? <span className='zocks-text-primary fw-medium paragraph-small'>Current</span> : null}
          </MobileMenuItem>
        )
      })}
    </>
  )

  return (
    <ExpandableMobileMenuButton menuItems={menuItems}>
      <div className='d-flex gap-2 align-items-center'>
        <i className='bi bi-columns-gap' />
        {selectedMeetingTemplate?.name ?? 'Change meeting type'}
      </div>
    </ExpandableMobileMenuButton>
  )
}

const MobileShareButton = () => {
  const { handleShare } = useHeaderShareButton()

  return (
    <MobileMenuButton onClick={handleShare}>
      <div className='d-flex gap-2 align-items-center'>
        <i className='bi bi-share' />
        Share
      </div>
    </MobileMenuButton>
  )
}

const MobileExportButton: React.FC = () => {
  const { currentUser, roomSession, selectedMeetingTemplate, showAddMeetingToCRMModal, selectedCRMServiceId, handleShowCRMModal, handleHideCRMModal } =
    useHeaderCRMExport()
  const { handleCopyAll } = useHeaderCopyAllResults()

  const crmIntegrations = (currentUser?.integrations ?? []).filter((i) => i.type === 'crm')

  const menuItems = (
    <>
      <MobileMenuItem onClick={handleCopyAll}>Copy all results</MobileMenuItem>
      {crmIntegrations.map((crmIntegration) => {
        const { serviceId } = crmIntegration
        return (
          <MobileMenuItem key={serviceId} onClick={() => handleShowCRMModal(serviceId)}>
            {`Save to ${serviceId.charAt(0).toUpperCase() + serviceId.slice(1)}`}
          </MobileMenuItem>
        )
      })}
    </>
  )

  return (
    <>
      {roomSession && showAddMeetingToCRMModal && selectedCRMServiceId && selectedMeetingTemplate ? (
        <AddMeetingToCRMModal
          serviceId={selectedCRMServiceId}
          roomSession={roomSession}
          meetingTemplate={selectedMeetingTemplate}
          show={showAddMeetingToCRMModal}
          onHide={handleHideCRMModal}
        />
      ) : null}
      <ExpandableMobileMenuButton menuItems={menuItems}>
        <div className='d-flex gap-2 align-items-center'>
          <i className='bi bi-download' />
          Export
        </div>
      </ExpandableMobileMenuButton>
    </>
  )
}

const MobileNotepadButton = () => {
  const { isLoading, handleOpenNotepad } = useHeaderNotepadButton()

  return (
    <ScopedBlock
      scope={{
        scope: 'meeting_note:read',
        superScope: ['meeting_note:admin'],
      }}
      missingScopeAction={MissingScopeAction.DISABLE}
    >
      <MobileMenuButton onClick={handleOpenNotepad} disabled={isLoading}>
        <div className='d-flex gap-2 align-items-center'>
          <i className='bi bi-journal-text' />
          Notepad
        </div>
      </MobileMenuButton>
    </ScopedBlock>
  )
}

const MobileCommentsButton = () => {
  const { isLoading, commentCount, handleOpenComments } = useHeaderCommentsButton()

  return (
    <ScopedBlock
      scope={{
        scope: 'comment:read',
        superScope: ['comment:admin'],
      }}
      missingScopeAction={MissingScopeAction.DISABLE}
    >
      <MobileMenuButton onClick={handleOpenComments} disabled={isLoading}>
        <div className='d-flex gap-2 align-items-center'>
          <i className='bi bi-chat-square-text' />
          Comments ({commentCount})
        </div>
      </MobileMenuButton>
    </ScopedBlock>
  )
}

const MobileExtraOptions: React.FC = () => {
  const { handleDeleteMeeting, modal, closeModal } = useHeaderExtraOptions()
  const menuItems = (
    <>
      <MobileMenuItem className='zocks-text-danger' onClick={handleDeleteMeeting}>
        Delete meeting
      </MobileMenuItem>
    </>
  )

  return (
    <>
      <DeleteMeetingModal show={modal === 'deleteMeeting'} onHide={closeModal} />
      <ExpandableMobileMenuButton menuItems={menuItems}>
        <div className='d-flex gap-2 align-items-center'>
          <i className='bi bi-three-dots-vertical' />
          Menu
        </div>
      </ExpandableMobileMenuButton>
    </>
  )
}

const MobileMenuButton: React.FC<ButtonProps> = ({ children, ...rest }) => {
  return (
    <div className='d-flex flex-column mobile-menu-item'>
      <Button variant='borderless' className='justify-content-between py-3 zocks-px-0.75' {...rest}>
        <div className='d-flex gap-2 align-items-center'>{children}</div>
      </Button>
    </div>
  )
}

interface ExpandableMobileMenuButtonProps extends ButtonProps {
  menuItems: ReactNode
}

const ExpandableMobileMenuButton: React.FC<PropsWithChildren<ExpandableMobileMenuButtonProps>> = ({ menuItems, children, onClick, ...rest }) => {
  const [expanded, setExpanded] = useState<boolean>(false)

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setExpanded((expanded) => !expanded)
    onClick?.(e)
  }

  return (
    <div className='d-flex flex-column mobile-menu-item'>
      <Button variant='borderless' className='justify-content-between py-3 zocks-px-0.75' onClick={handleClick} {...rest}>
        <div className='d-flex gap-2 align-items-center'>{children}</div>
        {expanded ? <i className='bi bi-chevron-contract' /> : <i className='bi bi-chevron-expand' />}
      </Button>
      {expanded ? <div className='d-flex flex-column gap-2'>{menuItems}</div> : null}
    </div>
  )
}

const MobileMenuItem: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className = '', children, ...rest }) => {
  return (
    <div className={`d-flex py-2 pe-2 ps-4 paragraph-small expandable-menu-item align-items-center gap-2 ${className}`} {...rest}>
      {children}
    </div>
  )
}
